import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import { useChatPanel } from 'src/contexts/ChatPanelContext';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import chevronDownSmallToken from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import sparklesSmallToken from '@amzn/meridian-tokens/base/icon/sparkles-small';
import Theme from '@amzn/meridian/theme';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import { Breadcrumbs } from 'src/components/common/Breadcrumbs';
import { useTranslation } from 'src/arb-wrapper';
import { Loader } from 'src/components/common/Loader';
import { FormCampaignEdit } from 'src/components/campaigns/forms/FormCampaignEdit';
import { CampaignDetailsView } from 'src/components/campaigns/CampaignDetailsView';
import { CampaignTitlesContainer } from 'src/components/campaigns/CampaignTitlesContainer';
import { AuditContent } from 'src/components/campaigns/AuditContent';
import { ProgramWorkflows } from 'src/components/workflows/ProgramWorkflows';
import { ExecutionWorkflows } from 'src/components/workflows/ExecutionWorkflows';
import { PublisherCampaignConfigTab } from 'src/components/campaigns/PublisherCampaignConfigTab';
import { AdsManagerTab } from 'src/components/entity-metrics/AdsManagerTab';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import {
  useCampaignsControllerGetQuery,
  useCampaignsControllerRefreshMutation,
  useProgramsControllerGetQuery,
  useCampaignConfigurationControllerFindManyQuery,
  useAgentSessionsControllerFindOneQuery,
} from 'src/store/leela-api';
import { useDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useCanPerform } from 'src/hooks/useCanPerform';
import { useDebounce } from 'src/hooks/useDebounce';
import { useAvailablePublishers } from 'src/hooks/use-available-publishers';
import { isFeatureEnabled } from 'src/feature-flags';
import { FEATURE_FLAGS, CreationSource, ConfigurationType } from 'src/constants';
import { CampaignPublishers } from 'src/store/constants';
import { isExecutionLayerUiEnabled } from 'src/utils/publishers';
import { Logger } from 'src/utils/logger';
import type { Publisher } from 'src/store/generated/leela-api-generated';

type CampaignTab = 'details' | 'audit' | 'workflows' | 'publisherConfig' | 'adsManager';

/** Non-Google publishers with an Ads Manager view, each gated by its own execution-layer flag. */
const EXECUTION_LAYER_ADS_MANAGER_PUBLISHERS: readonly Publisher[] = [
  CampaignPublishers.ADSP,
  CampaignPublishers.META,
  CampaignPublishers.TIKTOK,
];

const VALID_TABS: ReadonlySet<string> = new Set<CampaignTab>(['details', 'audit', 'workflows', 'publisherConfig', 'adsManager']);
const RESUME_SESSION_ARIA_TITLE_MAX_LENGTH = 40;

function truncateWithEllipsis (value: string, maxLength: number): string {
  const codePoints = Array.from(value);
  if (codePoints.length <= maxLength) return value;
  return `${codePoints.slice(0, maxLength - 1).join('')}…`;
}

/** Maps a CampaignTab value to its i18n translation key for breadcrumb display. */
const TAB_LABEL_KEYS: Record<CampaignTab, string> = {
  details: 'campaign_tab_details',
  audit: 'campaign_tab_audit',
  workflows: 'campaign_tab_workflows',
  publisherConfig: 'campaign_tab_publisher_config',
  adsManager: 'campaign_tab_ads_manager',
};

function parseTabParam (value: string | null): CampaignTab {
  if (value && VALID_TABS.has(value)) {
    return value as CampaignTab;
  }
  return 'details';
}

export const CampaignDetails: React.FC = () => {
  const { t } = useTranslation();
  const availablePublishers = useAvailablePublishers();
  const canManageCampaigns = useCanPerform('MANAGE_CAMPAIGNS');
  const [refreshCampaign, { isLoading: isRefreshing }] = useCampaignsControllerRefreshMutation();
  const { createToast } = useToasterContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = parseTabParam(searchParams.get('tab'));
  const [isEditing, setIsEditing] = useState(false);
  const [blocklistSearch, setBlocklistSearch] = useState('');
  const [isAgentMenuOpen, setIsAgentMenuOpen] = useState(false);
  const agentMenuRef = useRef();
  const {
    open: chatOpen,
    registerLocalSession,
    setOpen: setChatOpen,
  } = useChatPanel();
  const [debouncedBlocklistSearch] = useDebounce(blocklistSearch, 300) as [string, boolean];
  const sessionId = searchParams.get('sessionId');
  const hasSessionId = Boolean(sessionId);
  const { data: agentSession } = useAgentSessionsControllerFindOneQuery({
    sessionId: sessionId ?? '',
  }, {
    skip: !sessionId,
  });
  const trimmedResumeSessionTitle = agentSession?.title?.trim();
  const resumeSessionTitle = trimmedResumeSessionTitle === '' ? undefined : trimmedResumeSessionTitle;
  const accessibleResumeSessionTitle = resumeSessionTitle
    ? truncateWithEllipsis(resumeSessionTitle, RESUME_SESSION_ARIA_TITLE_MAX_LENGTH)
    : undefined;

  useEffect(() => {
    if (!hasSessionId) setIsAgentMenuOpen(false);
  }, [hasSessionId]);

  // Register the fresh session id locally before navigation so the Root URL-validation
  // effect skips it (the id is not yet persisted server-side); otherwise the not-yet-persisted
  // session would fail validation and the chat would close.
  const startNewAgentSession = () => {
    const newSessionId = crypto.randomUUID();
    const params = new URLSearchParams(searchParams);
    params.set('sessionId', newSessionId);
    registerLocalSession(newSessionId);
    setChatOpen(true);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Merge tab into existing params so sessionId (and any future params) survive tab switches
  const handleTabChange = useCallback((tab: CampaignTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'details') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    }, { replace: true });
    setIsEditing(false);
    setBlocklistSearch('');
  }, [setSearchParams]);

  const routeCampaignName = (location.state as { campaignName?: string })?.campaignName;

  const { data: campaign, isLoading, error } = useCampaignsControllerGetQuery({
    id: campaignId!,
  }, {
    skip: !campaignId,
  });

  const shouldShowCampaignAgentControls = canManageCampaigns &&
    !chatOpen &&
    campaign?.publisher === CampaignPublishers.GOOGLE &&
    isFeatureEnabled(FEATURE_FLAGS.ENABLE_GOOGLE_DEMANDGEN);
  const isPublisherConfigAvailable = availablePublishers.includes(CampaignPublishers.GOOGLE) && campaign?.publisher === CampaignPublishers.GOOGLE;
  // Ads Manager has views for Google, ADSP, Meta, and TikTok. Google is gated through availablePublishers;
  // the others through enableExecutionLayer.publishers.<publisher> (isExecutionLayerUiEnabled, fails closed).
  const isAdsManagerAvailable = campaign?.publisher === CampaignPublishers.GOOGLE
    ? availablePublishers.includes(CampaignPublishers.GOOGLE)
    : campaign?.publisher != null &&
      EXECUTION_LAYER_ADS_MANAGER_PUBLISHERS.includes(campaign.publisher) &&
      isExecutionLayerUiEnabled(campaign.publisher);
  // SCHEDULED_REFRESH runs in the execution layer, so only publishers routed through it can be force-synced.
  const canForceSyncCampaign = isExecutionLayerUiEnabled(campaign?.publisher);
  const activeTab = ((requestedTab === 'publisherConfig' && !isPublisherConfigAvailable) || (requestedTab === 'adsManager' && !isAdsManagerAvailable)) ? 'details' : requestedTab;

  const { data: program } = useProgramsControllerGetQuery(
    { id: campaign?.programId ?? '' },
    { skip: !campaign?.programId },
  );

  const { data: blocklistConfigs, isFetching: isBlocklistFetching } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.GTI_BLOCKLIST,
    source: CreationSource.USER,
    includeCampaignCount: false,
    pageSize: 20,
    page: 1,
    sort: ['updatedAt:-1'],
    ...(debouncedBlocklistSearch && { search: debouncedBlocklistSearch }),
  }, {
    skip: !isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST) || !isEditing,
  });

  // Separate query for the read-only detail view: fetch only the campaign's assigned blocklists by ID
  const { data: detailBlocklistConfigs } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.GTI_BLOCKLIST,
    includeCampaignCount: false,
    filters: { _id: { $in: campaign?.configIds ?? [] } },
  }, {
    skip: !isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST) || isEditing || !campaign?.configIds?.length,
  });

  // Read-only detail view: fetch the content rules attached to this campaign (by id from
  // configIds) so the information section can list their names.
  const { data: detailContentRuleConfigs } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.CONTENT_RULE,
    includeCampaignCount: false,
    filters: { _id: { $in: campaign?.configIds ?? [] } },
  }, {
    skip: !isFeatureEnabled(FEATURE_FLAGS.ENABLE_ASSET_EVAL_TRIGGER) || isEditing || !campaign?.configIds?.length,
  });

  useDocumentTitle(`Campaign ${campaignId} - Leela`);

  const displayName = campaign?.name ?? routeCampaignName ?? campaignId ?? '';

  const handleBack = () => { navigate('/campaigns'); };
  const handleViewAssets = () => { navigate(`/assets?campaignId=${campaignId}`); };
  const handleEdit = () => { setIsEditing(true); };
  const handleCancelEdit = () => { setIsEditing(false); setBlocklistSearch(''); };
  const handleSave = () => { setIsEditing(false); setBlocklistSearch(''); };

  // The backend answers 202: the refresh is queued, not performed, so the toast says "requested".
  const handleRefresh = async (): Promise<void> => {
    if (!campaignId) return;
    try {
      await refreshCampaign({ id: campaignId }).unwrap();
      createToast({ type: AlertType.SUCCESS, message: t('campaign_refresh_requested') });
    } catch (error) {
      const status = (error as { status?: number })?.status;
      const knownErrorMessage = status === 409
        ? t('campaign_refresh_conflict')
        : status === 422
          ? t('campaign_refresh_ineligible')
          : status === 403
            ? t('campaign_refresh_forbidden')
            : undefined;
      // rtk-query-base already logs 5xx responses via logAxiosError.
      if (!knownErrorMessage && !(typeof status === 'number' && status >= 500)) {
        Logger.error('Campaign refresh failed', { campaignId, error });
      }
      createToast({ type: AlertType.ERROR, message: knownErrorMessage ?? t('campaign_refresh_error') });
    }
  };

  if (!campaignId) {
    return (
      <Column>
        <Heading level={1}>{t('campaign_not_found_title')}</Heading>
        <p>{t('campaign_not_found_no_id')}</p>
        <Button type="secondary" onClick={handleBack}>
          {t('campaign_back_to_campaigns')}
        </Button>
      </Column>
    );
  }

  if (error) {
    return (
      <Column>
        <Heading level={1}>{t('campaign_error_loading_title')}</Heading>
        <p>{t('campaign_error_loading_message', { campaignId })}</p>
        <Button type="secondary" onClick={handleBack}>
          {t('campaign_back_to_campaigns')}
        </Button>
      </Column>
    );
  }

  // Preserve non-tab params (e.g. sessionId) when navigating via the campaign breadcrumb
  const persistedParams = new URLSearchParams(searchParams);
  persistedParams.delete('tab');
  const campaignPath = persistedParams.size > 0
    ? `/campaigns/${campaignId}?${persistedParams.toString()}`
    : `/campaigns/${campaignId}`;

  return (
    <Column>
      <Breadcrumbs
        items={[
          { label: t('campaigns_list_title'), path: '/campaigns' },
          { label: displayName, path: campaignPath },
          { label: t(TAB_LABEL_KEYS[activeTab]) },
        ]}
      />

      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Heading level={1}>{displayName}</Heading>
        <Row>
          {activeTab === 'details' && !isEditing && (
            <>
              <Button disabled={campaign?.status === 'ARCHIVED'} type="secondary" onClick={handleViewAssets}>
                {t('campaign_view_assets')}
              </Button>
              {canForceSyncCampaign && (
                <Button
                  data-testid="campaign-detail-refresh-button"
                  disabled={!canManageCampaigns || isRefreshing}
                  type="secondary"
                  onClick={handleRefresh}
                >
                  {t('campaign_refresh_button')}
                </Button>
              )}
              <Button
                data-testid="campaign-detail-edit-button"
                disabled={!canManageCampaigns || campaign?.status === 'ARCHIVED'}
                type="primary"
                onClick={handleEdit}
              >
                {t('form_global_edit')}
              </Button>
            </>
          )}
          {activeTab === 'details' && isEditing && (
            <Button data-testid="top-cancel-button" type="secondary" onClick={handleCancelEdit}>
              {t('form_global_cancel')}
            </Button>
          )}
          {shouldShowCampaignAgentControls && (
            <Theme tokens={{
              buttonBackgroundColorPrimaryDefault: '#7c3aed',
              buttonBackgroundColorPrimaryHover: '#6d28d9',
              buttonBackgroundColorPrimaryPressed: '#5b21b6',
            }}>
              <>
                <Button
                  aria-expanded={hasSessionId ? isAgentMenuOpen : undefined}
                  aria-haspopup={hasSessionId ? 'menu' : undefined}
                  data-testid="ai-campaign-builder-button"
                  ref={agentMenuRef}
                  type="primary"
                  onClick={() => {
                    if (hasSessionId) {
                      setIsAgentMenuOpen(true);
                    } else {
                      startNewAgentSession();
                    }
                  }}
                >
                  <Icon tokens={sparklesSmallToken} />{t('campaign_agent_open_button')}
                  {hasSessionId && <Icon tokens={chevronDownSmallToken} />}
                </Button>
                {hasSessionId && (
                  <Menu
                    anchorNode={agentMenuRef.current}
                    data-testid="ai-campaign-builder-menu"
                    minWidth={200}
                    open={isAgentMenuOpen}
                    position="bottom"
                    onClose={() => { setIsAgentMenuOpen(false); }}
                  >
                    <MenuItem
                      aria-label={accessibleResumeSessionTitle
                        ? t('campaign_agent_resume_session_with_title', { title: accessibleResumeSessionTitle })
                        : t('campaign_agent_resume_session')}
                      data-ai-campaign-builder-session-title={resumeSessionTitle}
                      data-testid="ai-campaign-builder-resume"
                      onClick={() => {
                        setIsAgentMenuOpen(false);
                        setChatOpen(true);
                      }}
                    >
                      {t('campaign_agent_resume_session')}
                    </MenuItem>
                    <MenuItem
                      data-testid="ai-campaign-builder-new-session"
                      onClick={() => {
                        setIsAgentMenuOpen(false);
                        startNewAgentSession();
                      }}
                    >
                      {t('campaign_agent_new_session')}
                    </MenuItem>
                  </Menu>
                )}
              </>
            </Theme>
          )}
        </Row>
      </Row>

      <TabGroup fill="line" value={activeTab} onChange={(tab: CampaignTab) => { handleTabChange(tab); }}>
        <Tab data-testid="campaign-tab-details" value="details">
          {t('campaign_tab_details')}
        </Tab>
        <Tab data-testid="campaign-tab-audit" value="audit">
          {t('campaign_tab_audit')}
        </Tab>
        <Tab data-testid="campaign-tab-workflows" value="workflows">
          {t('campaign_tab_workflows')}
        </Tab>
        {availablePublishers.includes(CampaignPublishers.GOOGLE) && campaign?.publisher === CampaignPublishers.GOOGLE && (
          <Tab data-testid="campaign-tab-publisher-config" value="publisherConfig">
            {t('campaign_tab_publisher_config')}
          </Tab>
        )}
        {isAdsManagerAvailable && (
          <Tab data-testid="campaign-tab-ads-manager" value="adsManager">
            {t('campaign_tab_ads_manager')}
          </Tab>
        )}
      </TabGroup>

      <Loader isLoading={isLoading}>
        {campaign && (
          <>
            {activeTab === 'details' && (
              <Column spacing="400">
                <Row width="100%">
                  {isEditing
                    ? (
                    <div style={{ width: '100%' }}>
                        <FormCampaignEdit
                          blocklistConfigs={blocklistConfigs}
                          blocklistSearch={blocklistSearch}
                          campaign={campaign}
                          isBlocklistLoading={isBlocklistFetching}
                          program={program}
                          programId={campaign.programId}
                          onBlocklistSearch={setBlocklistSearch}
                          onCancel={handleCancelEdit}
                          onSave={handleSave}
                        />
                    </div>
                      )
                    : (
                    <CampaignDetailsView blocklistConfigs={detailBlocklistConfigs} campaign={campaign} contentRuleConfigs={detailContentRuleConfigs} program={program} />
                      )}
                </Row>

                {!isEditing && campaign.status !== 'ARCHIVED' && (
                  <CampaignTitlesContainer
                    campaign={campaign}
                    campaignId={campaignId}
                  />
                )}
              </Column>
            )}

            {activeTab === 'audit' && program && (
              <AuditContent program={program} />
            )}

            {activeTab === 'workflows' && campaign?.programId && (
              // Execution-layer workflows: Google always; other publishers when enableExecutionLayer.publishers.<publisher> is on.
              isExecutionLayerUiEnabled(campaign.publisher)
                ? <ExecutionWorkflows campaignId={campaignId} />
                : <ProgramWorkflows campaignId={campaignId} programId={campaign.programId} />
            )}

            {activeTab === 'publisherConfig' && campaign && (
              <PublisherCampaignConfigTab campaign={campaign} />
            )}

            {activeTab === 'adsManager' && campaign && (
              <AdsManagerTab campaignId={campaign.campaignId!} publisher={campaign.publisher} />
            )}
          </>
        )}
      </Loader>
    </Column>
  );
};
